import { createPostGraphqlRequest } from '@tv2/lib-core/request';
import { timer } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import lodashFilter from 'lodash/fp/filter';
import lodashFlatMap from 'lodash/fp/flatMap';
import isEmpty from 'lodash/fp/isEmpty';
import lodashMinBy from 'lodash/fp/minBy';
import lodashPipe from 'lodash/fp/pipe';
import { EpgFragment, PlayUnifiedLiveEpgQuery } from './__generated__/Epg';
import {
  PlayUnifiedStartOverAvailable,
  PlayUnifiedStartOverTracking,
  PlayUnifiedStartOverTrackingQuery,
} from './__generated__/StartOver';
import { query } from './Epg';
import { UseTrackingProps } from '../../plugins/Tracking';
import { pollHeaders } from '../helpers';
import type { Media } from '../../types';

const minute = 60 * 1000;

const startOverTrackingQuery = {
  operationName: 'play_unified_start_over_tracking',
  query: PlayUnifiedStartOverTracking,
};

const startOverAvailableQuery = {
  operationName: 'play_unified_start_over_available',
  query: PlayUnifiedStartOverAvailable,
};

/**
 * Fetches EPG data from the `endpoint` given an entity `guid`.
 * @param   endpoint - URL of the graphql endpoint.
 * @param   guid     - Entity guid
 * @param   transmissionId - The transmission id to look up.
 * @returns Promise of graphql response.
 */
export function queryStartOver<T>(
  options: UseTrackingProps['epgOptions'],
  guid: string,
  transmissionId: string
) {
  return createPostGraphqlRequest<T>(
    options.endpoint,
    startOverTrackingQuery.query,
    { guid, transmissionId },
    startOverTrackingQuery.operationName,
    pollHeaders(options.clientPlatform)
  );
}

export function queryStartOverAvailability<T>(
  options: UseTrackingProps['epgOptions'],
  transmissionId: string
) {
  return createPostGraphqlRequest<T>(
    options.endpoint,
    startOverAvailableQuery.query,
    { transmissionId },
    startOverAvailableQuery.operationName,
    pollHeaders(options.clientPlatform)
  );
}

export const getCurrentOrNext = (
  data: EpgFragment['nodes'],
  currentId: string | null | undefined,
  time: number
) => {
  return lodashPipe(
    () => data,
    lodashFlatMap(x => (isEmpty(x?.subPrograms) ? x : x?.subPrograms)),
    lodashFilter(x => Boolean(x?.stop && x.stop >= time && x.transmissionId !== currentId)),
    lodashMinBy(x => x?.start)
  )();
};

export async function startOverNextEpgPoll(
  options: UseTrackingProps['epgOptions'],
  guid: string,
  transmissionId?: string | null
) {
  const a = await query<PlayUnifiedLiveEpgQuery>(options, guid, true);
  // eslint-disable-next-line no-underscore-dangle
  if (a.entity?.__typename !== 'Broadcast') {
    return null;
  }
  return getCurrentOrNext(a?.entity.epgEntries?.nodes ?? null, transmissionId, Date.now() / 1000);
}

/**
 * Return an observable that will poll for start over EPG data every 5 minutes
 */
export function startOverEpgPoll(options: UseTrackingProps['epgOptions'], media: Media) {
  return timer(0, 5 * minute).pipe(
    // Seams like this call is called more than once every 5 minutes
    switchMap(async () => {
      try {
        return queryStartOver<PlayUnifiedStartOverTrackingQuery>(
          options,
          media.metadata.guid!,
          media.metadata.mediaGuid!
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return null; // If we get an error we will return null and then that will be filtered out below
      }
    }),
    filter(isValidStartOverEpgResult),
    map(result => {
      return {
        tracking: result.tracking,
        epg: result?.epgEntry!.epg!,
      };
    })
  );
}

function isValidStartOverEpgResult(
  value: PlayUnifiedStartOverTrackingQuery | null | undefined
): value is PlayUnifiedStartOverTrackingQuery {
  return (
    value !== null &&
    value !== undefined &&
    value.epgEntry?.epg !== null &&
    value.epgEntry?.epg !== undefined
  );
}
